import { Injectable, inject, signal, NgZone, DestroyRef } from '@angular/core';
import { Router } from '@angular/router';
import {
  PATHS, INACTIVITY_TIMEOUT_MINUTES, INACTIVITY_COUNTDOWN_INTERVAL_MS,
  MILLISECONDS_PER_SECOND, SECONDS_PER_MINUTE, INACTIVITY_EVENTS
} from '@constants';
import { environment } from '@environments';

const TIMEOUT_SECONDS = INACTIVITY_TIMEOUT_MINUTES * SECONDS_PER_MINUTE;
const TIMEOUT_MS = TIMEOUT_SECONDS * MILLISECONDS_PER_SECOND;
const IS_DEV = environment.testMode;

@Injectable({ providedIn: 'root' })
export class InactivityService {
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _remainingSeconds = signal(TIMEOUT_SECONDS);
  readonly remainingSeconds = this._remainingSeconds.asReadonly();

  private readonly _active = signal(false);
  readonly active = this._active.asReadonly();

  private _originUrl = '';

  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private countdownId: ReturnType<typeof setInterval> | null = null;
  private lastActivityTime = Date.now();
  private boundResetTimer: (() => void) | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => this.stop());
  }

  start(): void {
    if (this._active()) {
      return;
    }

    this._active.set(true);
    this.lastActivityTime = Date.now();
    this._remainingSeconds.set(TIMEOUT_SECONDS);
    if (IS_DEV) {
      console.warn(`[Inactivity] started — timeout: ${INACTIVITY_TIMEOUT_MINUTES}min (${TIMEOUT_SECONDS}s)`);
    }

    this.ngZone.runOutsideAngular(() => {
      this.attachListeners();
      this.scheduleTimeout();
      this.startCountdown();
    });
  }

  stop(): void {
    if (IS_DEV) {
      console.warn('[Inactivity] stopped');
    }
    this._active.set(false);
    this.clearTimers();
    this.detachListeners();
    this._remainingSeconds.set(TIMEOUT_SECONDS);
  }

  private resetTimer(): void {
    const previousRemaining = this._remainingSeconds();
    this.lastActivityTime = Date.now();
    this._remainingSeconds.set(TIMEOUT_SECONDS);
    this.clearTimers();
    this.scheduleTimeout();
    this.startCountdown();
    if (IS_DEV) {
      console.warn(`[Inactivity] reset ${previousRemaining}s → ${TIMEOUT_SECONDS}s — activity detected`);
    }
  }

  private scheduleTimeout(): void {
    this.timeoutId = setTimeout(() => {
      this.ngZone.run(() => this.onTimeout());
    }, TIMEOUT_MS);
  }

  private startCountdown(): void {
    this.countdownId = setInterval(() => {
      const elapsed = Date.now() - this.lastActivityTime;
      const remaining = Math.max(
        TIMEOUT_SECONDS - Math.floor(elapsed / MILLISECONDS_PER_SECOND),
        0
      );
      if (remaining !== this._remainingSeconds()) {
        this._remainingSeconds.set(remaining);
        if (IS_DEV) {
          const minutes = Math.floor(remaining / SECONDS_PER_MINUTE);
          const seconds = remaining % SECONDS_PER_MINUTE;
          console.warn(`[Inactivity] ${minutes}m ${seconds}s remaining`);
        }
      }
    }, INACTIVITY_COUNTDOWN_INTERVAL_MS);
  }

  get originUrl(): string {
    return this._originUrl;
  }

  private onTimeout(): void {
    if (IS_DEV) {
      console.warn('[Inactivity] timeout reached — redirecting to inactivity page');
    }
    this._originUrl = this.router.url;
    this.stop();
    this.router.navigateByUrl('/' + PATHS.inactivity).then((success) => {
      if (IS_DEV) {
        if (success) {
          console.warn('[Inactivity] redirected to inactivity page');
        } else {
          console.warn('[Inactivity] navigation to inactivity page was blocked');
        }
      }
    });
  }

  private clearTimers(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.countdownId !== null) {
      clearInterval(this.countdownId);
      this.countdownId = null;
    }
  }

  private attachListeners(): void {
    this.boundResetTimer = () => this.resetTimer();
    for (const event of INACTIVITY_EVENTS) {
      document.addEventListener(event, this.boundResetTimer, { passive: true });
    }
  }

  private detachListeners(): void {
    if (this.boundResetTimer === null) {
      return;
    }
    for (const event of INACTIVITY_EVENTS) {
      document.removeEventListener(event, this.boundResetTimer);
    }
    this.boundResetTimer = null;
  }
}
